// Skeleton loaders shown via Next's loading.tsx during route transitions.
// All blocks are pure Tailwind with animate-pulse so they need no JS.

const bar = 'animate-pulse rounded-md bg-black/10';

function Bar({ className = '' }: { className?: string }) {
  return <div className={`${bar} ${className}`} />;
}

export function ListSkeleton() {
  return (
    <main className="bg-cream">
      {/* Hero */}
      <section className="page-container pt-12 text-center md:pt-[68px]">
        <Bar className="mx-auto h-10 w-[60%] md:h-16" />
        <Bar className="mx-auto mt-5 h-5 w-[40%]" />
        <Bar className="mx-auto my-10 h-[180px] w-full max-w-[1200px] md:h-[260px]" />
        <Bar className="mx-auto h-[58px] w-full max-w-[1140px] rounded-full" />
      </section>

      {/* List — 1/2/3-col vertical grid matching RestaurantCard */}
      <section className="page-container pb-20 pt-6">
        <Bar className="mb-6 h-6 w-[30%]" />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="mb-4 pb-4">
              <div className="Recommended-bx cutout-shape overflow-hidden">
                <Bar className="h-[180px] w-full !rounded-none" />
                <div className="p-4 h-100">
                  <Bar className="h-4 w-24" />
                  <Bar className="mt-4 h-7 w-[75%]" />
                  <div className="mt-4 flex items-center gap-2">
                    <Bar className="h-4 w-4" />
                    <Bar className="h-4 w-[55%]" />
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <Bar className="h-4 w-4" />
                    <Bar className="h-4 w-[40%]" />
                  </div>
                  <div className="mt-6">
                    <Bar className="h-9 w-28 rounded-full" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}

export function DetailSkeleton() {
  return (
    <main className="bg-cream">
      {/* Breadcrumb */}
      <div className="bg-plum-200 py-2">
        <div className="site-container">
          <Bar className="h-4 w-[40%] !bg-plum-900/20" />
        </div>
      </div>

      {/* Gallery */}
      <div className="site-container py-4">
        <div className="grid h-[260px] grid-cols-2 gap-0.5 overflow-hidden rounded-lg md:h-[432px] md:grid-cols-[2fr_1fr_1fr]">
          <Bar className="col-span-2 row-span-2 h-full w-full !rounded-none md:col-span-1 md:row-span-2" />
          {Array.from({ length: 4 }).map((_, i) => (
            <Bar key={i} className="h-full w-full !rounded-none" />
          ))}
        </div>
      </div>

      {/* Name + meta */}
      <div className="site-container pb-2 pt-4">
        <Bar className="h-9 w-[40%]" />
        <Bar className="mt-3 h-4 w-[55%]" />
      </div>

      {/* Body */}
      <div className="site-container py-8">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,795px)_380px]">
          <div className="space-y-10">
            {Array.from({ length: 3 }).map((_, i) => (
              <section key={i}>
                <Bar className="mb-4 h-8 w-[30%]" />
                <Bar className="h-32 w-full" />
              </section>
            ))}
          </div>
          <aside>
            <div className="rounded-xl bg-white p-6">
              <Bar className="h-7 w-[60%]" />
              <Bar className="mt-4 h-10 w-full" />
              <Bar className="mt-3 h-10 w-full" />
              <Bar className="mt-3 h-10 w-full" />
              <Bar className="mt-5 h-12 w-full rounded-full" />
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

export function CheckoutSkeleton() {
  return (
    <main className="bg-cream">
      <section className="site-container mb-10 pt-[30px]">
        <div className="grid gap-8 lg:grid-cols-[1fr_500px]">
          <div>
            <Bar className="h-8 w-[35%]" />
            <Bar className="mt-3 h-5 w-[70%]" />
            <div className="mt-2 rounded-[12px] border border-gray-200 bg-white p-5">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="mt-4 first:mt-0">
                  <Bar className="h-4 w-[20%]" />
                  <Bar className="mt-2 h-[46px] w-full rounded-md" />
                </div>
              ))}
              <Bar className="mt-6 h-[50px] w-full rounded-full md:h-[60px]" />
            </div>
          </div>

          <div>
            <div className="rounded-[12px] border border-gray-200 bg-white p-4 md:p-5">
              <Bar className="h-7 w-[40%]" />
              <div className="mt-5 flex gap-4">
                <Bar className="h-24 w-32 rounded-lg" />
                <div className="flex-1">
                  <Bar className="h-6 w-[60%]" />
                  <Bar className="mt-2 h-4 w-[80%]" />
                </div>
              </div>
              <Bar className="mt-6 h-5 w-[50%]" />
              <Bar className="mt-2 h-4 w-[60%]" />
              <Bar className="mt-5 h-5 w-[50%]" />
              <Bar className="mt-2 h-4 w-[40%]" />
              <hr className="my-4" />
              <Bar className="h-7 w-[60%]" />
            </div>
            <Bar className="mt-8 h-20 w-full rounded-[14px] !bg-yellow-200/60" />
          </div>
        </div>
      </section>
    </main>
  );
}

export function ConfirmationSkeleton() {
  return (
    <main className="bg-cream">
      <section className="site-container pt-[30px]">
        <div className="flex justify-center">
          <Bar className="h-20 w-20 !rounded-full" />
        </div>
        <Bar className="mx-auto mt-8 h-12 w-[50%]" />

        <div className="mt-12 grid gap-6 lg:grid-cols-[1.7fr_1fr]">
          <div className="rounded-xl bg-white p-8">
            <Bar className="h-6 w-[80%]" />
            <Bar className="mt-3 h-6 w-[60%]" />
            <Bar className="mt-3 h-6 w-[70%]" />
          </div>
          <div className="rounded-xl bg-white p-8 space-y-4">
            <Bar className="h-5 w-[60%]" />
            <Bar className="h-5 w-[60%]" />
            <Bar className="h-5 w-[60%]" />
          </div>
        </div>

        <Bar className="mb-3 mt-8 h-9 w-[40%]" />

        <div className="mt-4 grid gap-6 lg:grid-cols-2">
          <Bar className="h-[200px] w-full rounded-xl" />
          <Bar className="h-[200px] w-full rounded-xl" />
        </div>
      </section>
    </main>
  );
}

/**
 * Single restaurant card skeleton — mirrors the sports-style card
 * (no image, cutout-shape body with category / heading / location
 * rows / Book button) so the infinite-scroll loader doesn't jump the
 * layout when real cards swap in.
 */
export function RestaurantCardSkeleton() {
  return (
    <div className="mb-4 pb-4">
      <div className="Recommended-bx cutout-shape overflow-hidden">
        <Bar className="h-[180px] w-full !rounded-none" />
        <div className="p-4 h-100">
          <Bar className="h-4 w-24" />
          <Bar className="mt-4 h-7 w-[75%]" />
          <div className="mt-4 flex items-center gap-2">
            <Bar className="h-4 w-4" />
            <Bar className="h-4 w-[55%]" />
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Bar className="h-4 w-4" />
            <Bar className="h-4 w-[40%]" />
          </div>
          <div className="mt-6">
            <Bar className="h-9 w-28 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function TableSelectionSkeleton() {
  return (
    <main className="bg-cream">
      <section className="site-container mb-10 pt-[30px]">
        <Bar className="h-8 w-[40%]" />
        <Bar className="mt-3 h-5 w-[60%]" />
        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Bar key={i} className="h-[180px] w-full rounded-xl" />
          ))}
        </div>
      </section>
    </main>
  );
}
